import { FadedText } from '@app/components/base/FadedText';
import { useAuditMention } from '@app/modules/audit/modules/station-audit/hooks/useAuditMention';
import { AuditLiveMention } from '@app/modules/audit/modules/station-audit/types';
import { CopyLinkAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/CopyLinkAtTimestampButton';
import { PlayAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/PlayAtTimestampButton';
import { StreamPopup } from '@app/modules/logger/screens/stream/components/popup/StreamPopup';
import { StreamPopupHeader } from '@app/modules/logger/screens/stream/components/popup/StreamPopupHeader';
import { AuditLiveMentionConfidence, toTimestamp } from '@mi-front/core';
import KeyboardVoiceOutlinedIcon from '@mui/icons-material/KeyboardVoiceOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import { Chip, Typography } from '@mui/material';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { MentionConfidenceButtons } from './MentionConfidenceButtons';
import { MentionShowButton } from './MentionShowButton';

const DESCRIPTION_MAX_HEIGHT_PX = 100;
const MENTION_MATCH_MESSAGE = defineMessage({
  id: 'audit.liveMentions.mentionMatch',
  defaultMessage: 'Mention Match',
});
const REJECTED_MESSAGE = defineMessage({
  id: 'audit.liveMentions.rejected',
  defaultMessage: 'Rechazada',
});

/**
 * Displays a mention summary, confidence controls, and stream actions.
 */
export function MentionInfo({ liveMention }: { liveMention: AuditLiveMention }) {
  const { $t } = useIntl();
  const { stationId, id, datetime, name, description } = liveMention;
  const time = toTimestamp(datetime);
  const {
    data: updatedLiveMention,
    isLoading,
    confidenceMutation,
  } = useAuditMention(stationId, id);
  const confidence =
    updatedLiveMention?.confidence ?? liveMention.confidence ?? AuditLiveMentionConfidence.AUTO;
  const isRejected = confidence === AuditLiveMentionConfidence.REJECTED;

  return (
    <StreamPopup
      minWidth={280}
      maxWidth={360}
      header={
        <StreamPopupHeader
          icon={<KeyboardVoiceOutlinedIcon />}
          title={$t(MENTION_MATCH_MESSAGE)}
          time={datetime}
        />
      }
      footer={
        <>
          <PlayAtTimestampButton time={time} />
          <CopyLinkAtTimestampButton time={time} />
          <MentionShowButton liveMention={liveMention} />
          {!isRejected && (
            <MentionConfidenceButtons
              confidence={confidence}
              isLoading={isLoading}
              onConfidenceChange={(updatedConfidence) =>
                confidenceMutation.mutate(updatedConfidence)
              }
            />
          )}
        </>
      }
    >
      <Typography
        variant="subtitle2"
        sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}
      >
        {name}
      </Typography>

      {description && (
        <FadedText maxHeight={DESCRIPTION_MAX_HEIGHT_PX}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}
          >
            {description}
          </Typography>
        </FadedText>
      )}

      {isRejected && (
        <Chip
          size="small"
          variant="outlined"
          color="error"
          icon={<ThumbDownOutlinedIcon fontSize="inherit" />}
          label={<FormattedMessage {...REJECTED_MESSAGE} />}
        />
      )}
    </StreamPopup>
  );
}
